import { useMemo, useCallback } from 'react';
import { clsx } from 'clsx';
import {
  ContainerSize,
  ContainerRack,
  InfrastructureUnit,
  CONTAINER_DIMENSIONS,
  getRackDimensions,
  InfrastructureType,
} from '../../types/configurator';
import { INFRASTRUCTURE_COLORS } from '../../data/infrastructure';
import {
  DndContext,
  DragEndEvent,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Server, Plug, Battery, Snowflake, Flame, Cable, Eye, Monitor, Layers, Construction } from 'lucide-react';

// Scale: 1px = 10mm
const SCALE = 10;
const DEFAULT_GRID_SNAP_MM = 100; // 100mm grid snap

// Snap position to grid
function snapToGrid(value: number, gridSize: number): number {
  return Math.round(value / gridSize) * gridSize;
}

interface ContainerFloorPlanProps {
  containerSize: ContainerSize;
  racks: ContainerRack[];
  infrastructure: InfrastructureUnit[];
  selectedRackId?: string;
  selectedInfraId?: string;
  onRackSelect: (rackId: string) => void;
  onRackMove: (rackId: string, position: { x: number; y: number }) => void;
  onInfrastructureSelect: (infraId: string) => void;
  onInfrastructureMove: (infraId: string, position: { x: number; y: number }) => void;
  showGrid?: boolean;
  gridSnapMm?: number; // Grid snap size in mm
}

// Infrastructure type to icon mapping
const INFRA_ICONS: Record<InfrastructureType, typeof Plug> = {
  'pdu': Plug,
  'ups': Battery,
  'cooling': Snowflake,
  'fire-suppression': Flame,
  'cable-tray': Cable,
  'monitoring': Eye,
  'workspace': Monitor,
  'insulation': Layers,
  'structural': Construction,
};

// Draggable Rack Component
function DraggableRack({
  rack,
  isSelected,
  scale,
}: {
  rack: ContainerRack;
  isSelected: boolean;
  scale: number;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `rack-${rack.id}`,
    data: { type: 'rack', rack },
  });

  const dims = getRackDimensions(rack.type);
  const width = dims.widthMm / scale;
  const depth = dims.depthMm / scale;

  // Apply rotation
  const [w, h] = rack.rotation === 90 || rack.rotation === 270 ? [depth, width] : [width, depth];

  const style: React.CSSProperties = {
    position: 'absolute',
    left: rack.position.x / scale,
    top: rack.position.y / scale,
    width: w,
    height: h,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : isSelected ? 50 : 10,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={clsx(
        'flex cursor-grab flex-col items-center justify-center rounded border-2 bg-gray-700 text-white active:cursor-grabbing',
        isSelected ? 'border-hermit-500 ring-2 ring-hermit-300' : 'border-gray-500',
        isDragging && 'shadow-lg'
      )}
    >
      <Server className="h-4 w-4" />
      <span className="mt-1 text-[8px] font-medium">{rack.name}</span>
      <span className="text-[7px] text-gray-300">{rack.type === 'rack-42' ? '42U' : '18U'}</span>
    </div>
  );
}

// Draggable Infrastructure Component
function DraggableInfrastructure({
  infra,
  isSelected,
  scale,
}: {
  infra: InfrastructureUnit;
  isSelected: boolean;
  scale: number;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `infra-${infra.id}`,
    data: { type: 'infrastructure', infra },
  });

  // Skip rack-mounted infrastructure (rendered inside rack)
  if (infra.rackId) return null;

  const baseWidth = Math.max(infra.specs.widthMm / scale, 30);
  const baseDepth = Math.max(infra.specs.depthMm / scale, 30);
  const rotation = infra.rotation || 0;

  // Swap dimensions for 90/270 degree rotations
  const [width, depth] = rotation === 90 || rotation === 270 ? [baseDepth, baseWidth] : [baseWidth, baseDepth];

  const style: React.CSSProperties = {
    position: 'absolute',
    left: infra.position.x / scale,
    top: infra.position.y / scale,
    width,
    height: depth,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : isSelected ? 50 : 5,
  };

  const Icon = INFRA_ICONS[infra.type];
  const colors = INFRASTRUCTURE_COLORS[infra.type];

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={clsx(
        'flex cursor-grab flex-col items-center justify-center rounded border-2 active:cursor-grabbing',
        colors.bg,
        isSelected ? 'border-hermit-500 ring-2 ring-hermit-300' : colors.border,
        isDragging && 'shadow-lg'
      )}
      title={`${infra.name} (${rotation}°)`}
    >
      <Icon className={clsx('h-3 w-3', colors.text)} style={{ transform: `rotate(${rotation}deg)` }} />
      <span className={clsx('mt-0.5 truncate text-[7px] font-medium', colors.text)}>
        {infra.type.toUpperCase()}
      </span>
    </div>
  );
}

// Drop zone for the container floor
function ContainerDropZone({
  containerSize,
  children,
}: {
  containerSize: ContainerSize;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'container-floor',
  });

  const dims = CONTAINER_DIMENSIONS[containerSize];
  const width = dims.lengthMm / SCALE;
  const height = dims.widthMm / SCALE;

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'relative rounded-lg border-4 bg-gray-100 transition-colors',
        isOver ? 'border-hermit-400 bg-hermit-50' : 'border-gray-400'
      )}
      style={{ width, height }}
    >
      {children}
    </div>
  );
}

export function ContainerFloorPlan({
  containerSize,
  racks,
  infrastructure,
  selectedRackId,
  selectedInfraId,
  onRackSelect,
  onRackMove,
  onInfrastructureSelect,
  onInfrastructureMove,
  showGrid = true,
  gridSnapMm = DEFAULT_GRID_SNAP_MM,
}: ContainerFloorPlanProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const dims = CONTAINER_DIMENSIONS[containerSize];
  const width = dims.lengthMm / SCALE;
  const height = dims.widthMm / SCALE;

  // Grid lines
  const gridLines = useMemo(() => {
    if (!showGrid) return null;

    const lines: JSX.Element[] = [];
    const gridSize = 100 / SCALE; // 100mm grid

    // Vertical lines
    for (let x = gridSize; x < width; x += gridSize) {
      lines.push(
        <line
          key={`v-${x}`}
          x1={x}
          y1={0}
          x2={x}
          y2={height}
          stroke="#e5e7eb"
          strokeWidth="0.5"
        />
      );
    }

    // Horizontal lines
    for (let y = gridSize; y < height; y += gridSize) {
      lines.push(
        <line
          key={`h-${y}`}
          x1={0}
          y1={y}
          x2={width}
          y2={y}
          stroke="#e5e7eb"
          strokeWidth="0.5"
        />
      );
    }

    return lines;
  }, [width, height, showGrid]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta } = event;
      const id = active.id as string;
      const dims = CONTAINER_DIMENSIONS[containerSize];

      if (id.startsWith('rack-')) {
        const rackId = id.replace('rack-', '');
        const rack = racks.find((r) => r.id === rackId);
        if (rack) {
          const rawX = rack.position.x + delta.x * SCALE;
          const rawY = rack.position.y + delta.y * SCALE;
          // Snap to grid
          const newX = snapToGrid(Math.max(0, Math.min(rawX, dims.lengthMm - 100)), gridSnapMm);
          const newY = snapToGrid(Math.max(0, Math.min(rawY, dims.widthMm - 100)), gridSnapMm);
          onRackMove(rackId, { x: newX, y: newY });
        }
      } else if (id.startsWith('infra-')) {
        const infraId = id.replace('infra-', '');
        const infra = infrastructure.find((i) => i.id === infraId);
        if (infra) {
          const rawX = infra.position.x + delta.x * SCALE;
          const rawY = infra.position.y + delta.y * SCALE;
          // Snap to grid
          const newX = snapToGrid(Math.max(0, Math.min(rawX, dims.lengthMm - 100)), gridSnapMm);
          const newY = snapToGrid(Math.max(0, Math.min(rawY, dims.widthMm - 100)), gridSnapMm);
          onInfrastructureMove(infraId, { x: newX, y: newY });
        }
      }
    },
    [racks, infrastructure, onRackMove, onInfrastructureMove, containerSize, gridSnapMm]
  );


  return (
    <div className="overflow-auto rounded-lg border border-gray-200 bg-white p-4">
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
      >
        <ContainerDropZone containerSize={containerSize}>
          {/* Grid overlay */}
          {showGrid && (
            <svg
              className="pointer-events-none absolute inset-0"
              width={width}
              height={height}
            >
              {gridLines}
            </svg>
          )}

          {/* Container doors (at one end) */}
          <div
            className="absolute left-0 top-0 h-full w-2 bg-amber-600"
            style={{ borderRadius: '4px 0 0 4px' }}
            title="Container doors"
          />

          {/* Airflow indicator */}
          <div className="absolute right-2 top-2 flex items-center gap-1 rounded bg-white/80 px-2 py-1 text-[10px] text-gray-500">
            <span>Airflow</span>
            <span className="text-blue-500">→</span>
          </div>

          {/* Dimension labels */}
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-gray-400">
            {dims.lengthMm / 1000}m
          </div>
          <div
            className="absolute -right-4 top-1/2 -translate-y-1/2 -rotate-90 text-[10px] text-gray-400"
            style={{ transformOrigin: 'center' }}
          >
            {dims.widthMm / 1000}m
          </div>

          {/* Racks */}
          {racks.map((rack) => (
            <div
              key={rack.id}
              data-rack-id={rack.id}
              onClick={() => onRackSelect(rack.id)}
            >
              <DraggableRack
                rack={rack}
                isSelected={rack.id === selectedRackId}
                scale={SCALE}
              />
            </div>
          ))}

          {/* Floor-mounted infrastructure */}
          {infrastructure
            .filter((i) => !i.rackId)
            .map((infra) => (
              <div
                key={infra.id}
                data-infra-id={infra.id}
                onClick={() => onInfrastructureSelect(infra.id)}
              >
                <DraggableInfrastructure
                  infra={infra}
                  isSelected={infra.id === selectedInfraId}
                  scale={SCALE}
                />
              </div>
            ))}
        </ContainerDropZone>
      </DndContext>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] text-gray-500">
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded border border-gray-500 bg-gray-700" />
          <span>Rack</span>
        </div>
        {(['pdu', 'ups', 'cooling', 'fire-suppression', 'monitoring'] as InfrastructureType[]).map(
          (type) => {
            const colors = INFRASTRUCTURE_COLORS[type];
            const Icon = INFRA_ICONS[type];
            return (
              <div key={type} className="flex items-center gap-1">
                <div className={clsx('h-3 w-3 rounded border', colors.bg, colors.border)}>
                  <Icon className={clsx('h-full w-full p-0.5', colors.text)} />
                </div>
                <span className="capitalize">{type.replace('-', ' ')}</span>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
}
